import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { db } from '@/services/firebase'
import { ref as dbRef, onValue, push, remove, update, child, onDisconnect, set } from 'firebase/database'
import { useSessionStore } from './session'

export const useTableStore = defineStore('table', () => {
  const sessionStore = useSessionStore()
  
  const cart = ref({})
  const users = ref({})
  const presence = ref({})
  const isCartOpen = ref(false)
  
  // Payment related state
  const paymentSessionId = ref(null)
  const paymentStatus = ref(null) // 'pending', 'processing', 'completed', 'failed'
  const lastOrderId = ref(null)
  
  let cartUnsubscribe = null
  let usersUnsubscribe = null
  let presenceUnsubscribe = null

  // Getters
  const cartItems = computed(() => {
    return Object.entries(cart.value).map(([key, value]) => ({
      ...value,
      id: key // Ensure the Firebase key is part of the object
    }))
  })

  const cartTotal = computed(() => {
    return cartItems.value.reduce((total, item) => total + (item.price * item.quantity), 0)
  })

  const itemCount = computed(() => {
    return cartItems.value.reduce((total, item) => total + item.quantity, 0)
  })

  const activeUsers = computed(() => {
    return Object.values(presence.value)
  })

  // Actions
  const bindSession = (sessionId) => {
    if (!sessionId) return

    const sessionRef = dbRef(db, `sessions/${sessionId}`)
    
    // Listen to Cart
    cartUnsubscribe = onValue(child(sessionRef, 'cart'), (snapshot) => {
      cart.value = snapshot.val() || {}
    })

    // Listen to Users
    usersUnsubscribe = onValue(child(sessionRef, 'users'), (snapshot) => {
      users.value = snapshot.val() || {}
    })

    // Listen to Presence
    presenceUnsubscribe = onValue(child(sessionRef, 'presence'), (snapshot) => {
      presence.value = snapshot.val() || {}
    })

    // Register own presence
    if (sessionStore.currentUser) {
      const myPresenceRef = child(sessionRef, `presence/${sessionStore.currentUser.id}`)
      const myPresence = {
        id: sessionStore.currentUser.id,
        name: sessionStore.currentUser.name,
        onlineAt: Date.now()
      }
      
      set(myPresenceRef, myPresence)
      onDisconnect(myPresenceRef).remove()
    }
  }

  const unbindTable = () => {
    if (cartUnsubscribe) cartUnsubscribe()
    if (usersUnsubscribe) usersUnsubscribe()
    if (presenceUnsubscribe) presenceUnsubscribe()
    
    // Remove own presence manually on unbind
    if (sessionStore.sessionId && sessionStore.currentUser) {
      const myPresenceRef = dbRef(db, `sessions/${sessionStore.sessionId}/presence/${sessionStore.currentUser.id}`)
      remove(myPresenceRef)
    }

    cart.value = {}
    users.value = {}
    presence.value = {}
  }

  const addItemToCart = async (product, customizations, finalPrice) => {
    if (!sessionStore.sessionId) return
    if (!sessionStore.currentUser) throw new Error('No user identified')

    const cartItem = {
      productId: product.id,
      name: product.name,
      price: finalPrice,
      quantity: 1,
      addedBy: sessionStore.currentUser.id,
      addedByName: sessionStore.currentUser.name,
      customizations: customizations,
      addedAt: Date.now()
    }

    const cartRef = child(dbRef(db), `sessions/${sessionStore.sessionId}/cart`)
    await push(cartRef, cartItem)
  }

  const removeItem = async (itemId) => {
    if (!sessionStore.sessionId) return
    await remove(dbRef(db, `sessions/${sessionStore.sessionId}/cart/${itemId}`))
  }

  const updateQuantity = async (itemId, quantity) => {
    if (!sessionStore.sessionId) return
    if (quantity <= 0) {
      await removeItem(itemId)
      return
    }
    await update(dbRef(db, `sessions/${sessionStore.sessionId}/cart/${itemId}`), { quantity })
  }

  const clearCart = async () => {
    if (!sessionStore.sessionId) return
    await remove(dbRef(db, `sessions/${sessionStore.sessionId}/cart`))
  }

  const transferHost = async (currentHostId, newHostId) => {
    if (!sessionStore.sessionId) return
    
    const updates = {}
    updates[`sessions/${sessionStore.sessionId}/users/${currentHostId}/isHost`] = false
    updates[`sessions/${sessionStore.sessionId}/users/${newHostId}/isHost`] = true
    
    await update(dbRef(db), updates)
  }

  const toggleCart = () => {
    isCartOpen.value = !isCartOpen.value
  }

  // Payment related actions
  const setPaymentSession = (sessionId) => {
    paymentSessionId.value = sessionId
    paymentStatus.value = 'pending'
  }

  const completeOrder = async (transactionId) => {
    if (!sessionStore.sessionId) return

    try {
      // Store order completion in Firebase
      const ordersRef = dbRef(db, `sessions/${sessionStore.sessionId}/orders`)
      const orderData = {
        transactionId: transactionId,
        items: cart.value,
        total: cartTotal.value,
        completedAt: Date.now(),
        completedBy: sessionStore.currentUser?.id || 'unknown'
      }
      
      const newOrderRef = await push(ordersRef, orderData)
      lastOrderId.value = newOrderRef.key
      
      // Update session payment status
      await update(dbRef(db, `sessions/${sessionStore.sessionId}`), {
        paymentStatus: 'completed',
        lastPaymentAt: Date.now(),
        lastTransactionId: transactionId
      })

      // Clear the cart after successful order
      await clearCart()
      
      paymentStatus.value = 'completed'
      paymentSessionId.value = null
    } catch (error) {
      console.error('Error completing order:', error)
      paymentStatus.value = 'failed'
      throw error
    }
  }

  const clearPaymentState = () => {
    paymentSessionId.value = null
    paymentStatus.value = null
  }

  const clearLocalState = () => {
    cart.value = {}
    users.value = {}
    presence.value = {}
    paymentSessionId.value = null
    paymentStatus.value = null
    lastOrderId.value = null
    isCartOpen.value = false
  }

  return {
    cart,
    users,
    isCartOpen,
    cartItems,
    cartTotal,
    itemCount,
    bindSession,
    unbindTable,
    addItemToCart,
    removeItem,
    updateQuantity,
    clearCart,
    transferHost,
    toggleCart,
    activeUsers,
    // Payment exports
    paymentSessionId,
    paymentStatus,
    lastOrderId,
    setPaymentSession,
    completeOrder,
    clearPaymentState,
    clearLocalState
  }
})
